import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import {
  TrendingUp,
  Users,
  DollarSign,
  Activity,
  Award,
  Zap,
  BarChart3,
  LineChart as LineChartIcon
} from 'lucide-react';

const API_URL = window.__BACKEND_URL;

export default function InternalDashboard() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Dashboard data
  const [appKPIs, setAppKPIs] = useState(null);
  const [plansSummary, setPlansSummary] = useState([]);
  const [engagement, setEngagement] = useState(null);
  const [streaks, setStreaks] = useState(null);
  const [monetization, setMonetization] = useState(null);
  const [selfMarketing, setSelfMarketing] = useState([]);
  const [valuation, setValuation] = useState(null);
  
  // View mode (for future investor/lender mode)
  const [viewMode, setViewMode] = useState('full'); // 'full' or 'investor_readonly'

  useEffect(() => {
    loadAllDashboardData();
  }, []);

  const loadAllDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Load all dashboard data in parallel
      const [
        kpisRes,
        plansRes,
        engagementRes,
        streaksRes,
        monetizationRes,
        selfMarketingRes,
        valuationRes
      ] = await Promise.all([
        axios.get(`${API_URL}/api/internal-dashboard/app-kpis`, { headers }),
        axios.get(`${API_URL}/api/internal-dashboard/plans-summary`, { headers }),
        axios.get(`${API_URL}/api/internal-dashboard/engagement`, { headers }),
        axios.get(`${API_URL}/api/internal-dashboard/streaks`, { headers }),
        axios.get(`${API_URL}/api/internal-dashboard/monetization-forecast`, { headers }),
        axios.get(`${API_URL}/api/internal-dashboard/self-marketing-performance`, { headers }),
        axios.get(`${API_URL}/api/internal-dashboard/valuation-snapshots?app_key=consistency`, { headers })
      ]);
      
      setAppKPIs(kpisRes.data);
      setPlansSummary(plansRes.data);
      setEngagement(engagementRes.data);
      setStreaks(streaksRes.data);
      setMonetization(monetizationRes.data);
      setSelfMarketing(selfMarketingRes.data);
      setValuation(valuationRes.data);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Error loading dashboard: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const seedValuationData = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/internal-dashboard/seed-valuation-data`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadAllDashboardData();
    } catch (err) {
      console.error('Error seeding valuation data:', err);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  if (loading && !appKPIs) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading dashboard...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent mb-2">
              Internal Dashboard
            </h1>
            <p className="text-gray-400">Owner / Investor View • Consistency App</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode(viewMode === 'full' ? 'investor_readonly' : 'full')}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-gray-300 hover:bg-white/10 transition-colors"
            >
              {viewMode === 'full' ? '👁️ Full View' : '👥 Investor View'}
            </button>
            <button
              onClick={loadAllDashboardData}
              disabled={loading}
              className="px-4 py-2 bg-cyan-500/20 border border-cyan-500/30 rounded-lg text-sm text-cyan-300 hover:bg-cyan-500/30 transition-colors disabled:opacity-50"
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-300">
            {error}
          </div>
        )}

        {/* A) High-Level App KPIs */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-cyan-400" />
            App KPIs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            <KPICard
              title="Total Users"
              value={appKPIs?.total_users || 0}
              format="number"
              icon={<Users className="w-5 h-5" />}
              color="blue"
            />
            <KPICard
              title="Active Paying Orgs"
              value={appKPIs?.active_paying_orgs || 0}
              format="number"
              icon={<Activity className="w-5 h-5" />}
              color="green"
            />
            <KPICard
              title="MRR"
              value={appKPIs?.mrr || 0}
              format="currency"
              icon={<DollarSign className="w-5 h-5" />}
              color="emerald"
              note="Estimated"
            />
            <KPICard
              title="ARR"
              value={appKPIs?.arr || 0}
              format="currency"
              icon={<DollarSign className="w-5 h-5" />}
              color="cyan"
              note="Estimated"
            />
            <KPICard
              title="Churn (30d)"
              value={appKPIs?.churn_rate_30d || 0}
              format="percentage"
              icon={<Activity className="w-5 h-5" />}
              color="yellow"
              note="Approximate"
            />
            <KPICard
              title="Total Orgs"
              value={appKPIs?.total_orgs || 0}
              format="number"
              icon={<Users className="w-5 h-5" />}
              color="purple"
            />
          </div>
        </div>

        {/* B) Users & Plans Summary */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-purple-400" />
            Plans Distribution
          </h2>
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left border-b border-white/10">
                    <th className="pb-3 text-gray-400 font-medium">Plan</th>
                    <th className="pb-3 text-gray-400 font-medium text-right">Organizations</th>
                    <th className="pb-3 text-gray-400 font-medium text-right">% of Total</th>
                    <th className="pb-3 text-gray-400 font-medium text-right">Est. MRR</th>
                    <th className="pb-3 text-gray-400 font-medium">Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  {plansSummary.map((plan, idx) => (
                    <tr key={idx} className="border-b border-white/5">
                      <td className="py-3 text-white font-medium">{plan.plan_name}</td>
                      <td className="py-3 text-right text-white">{plan.org_count}</td>
                      <td className="py-3 text-right text-gray-300">{plan.percentage}%</td>
                      <td className="py-3 text-right text-emerald-400">{formatCurrency(plan.est_mrr)}</td>
                      <td className="py-3">
                        <div className="w-full bg-white/5 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full"
                            style={{ width: `${plan.percentage}%` }}
                          ></div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* C) Engagement & Usage */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Activity className="w-6 h-6 text-green-400" />
            Engagement & Usage
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Metrics Cards */}
            <div className="grid grid-cols-2 gap-4">
              <MetricCard title="Total Creators" value={engagement?.total_creators || 0} />
              <MetricCard title="Clips This Month" value={engagement?.clips_this_month || 0} />
              <MetricCard title="Optimized Variants" value={engagement?.optimized_this_month || 0} />
              <MetricCard title="Scheduled Posts" value={engagement?.scheduled_this_month || 0} />
            </div>
            
            {/* Daily Chart */}
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Last 30 Days Activity</h3>
              <div className="space-y-2">
                {engagement?.daily_data?.slice(-7).map((day, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <span className="text-xs text-gray-400 w-24">{day.date}</span>
                    <div className="flex-1 flex gap-2">
                      <div
                        className="bg-cyan-500/30 rounded h-6 flex items-center justify-center text-xs text-cyan-300"
                        style={{ width: `${(day.scheduled / 20) * 100}%`, minWidth: day.scheduled > 0 ? '30px' : '0' }}
                      >
                        {day.scheduled > 0 && day.scheduled}
                      </div>
                      <div
                        className="bg-green-500/30 rounded h-6 flex items-center justify-center text-xs text-green-300"
                        style={{ width: `${(day.posted / 20) * 100}%`, minWidth: day.posted > 0 ? '30px' : '0' }}
                      >
                        {day.posted > 0 && day.posted}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex gap-4 text-xs text-gray-400">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-cyan-500/30 rounded"></div>
                  Scheduled
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500/30 rounded"></div>
                  Posted
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* D) Streaks & Consistency */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Award className="w-6 h-6 text-yellow-400" />
            Streaks & Consistency
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Streak Stats */}
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Streak Stats</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-400">Average Current Streak</p>
                  <p className="text-3xl font-bold text-cyan-400">{streaks?.avg_current_streak || 0} days</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Longest Current Streak</p>
                  <p className="text-3xl font-bold text-yellow-400">{streaks?.longest_current_streak || 0} days</p>
                </div>
              </div>
            </div>

            {/* Badge Distribution */}
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Badge Distribution</h3>
              <div className="space-y-2">
                {Object.entries(streaks?.badge_distribution || {}).map(([badge, count]) => (
                  <div key={badge} className="flex justify-between items-center">
                    <span className="text-sm text-gray-300 capitalize">{badge}</span>
                    <span className="text-sm font-semibold text-white">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Creators */}
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Top Creators by Streak</h3>
              <div className="space-y-2">
                {streaks?.top_creators?.slice(0, 5).map((creator, idx) => (
                  <div key={idx} className="flex justify-between items-center text-sm">
                    <div className="flex-1 truncate">
                      <span className="text-white font-medium">{creator.creator_name}</span>
                      {viewMode === 'full' && (
                        <span className="text-gray-400 text-xs ml-2">({creator.org_name})</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-cyan-400 font-semibold">{creator.current_streak}d</span>
                      <span className="text-xs text-gray-400 capitalize">{creator.badge}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* E) Monetization Forecast */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Zap className="w-6 h-6 text-emerald-400" />
            Monetization Forecast
            <span className="text-sm font-normal text-gray-400 ml-2">(Internal Strategy Only)</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Est. Monthly Creator Revenue"
              value={monetization?.total_est_monthly_creator_revenue || 0}
              format="currency"
              color="emerald"
            />
            <MetricCard
              title="Creators $1K+ /mo"
              value={monetization?.creators_1k_plus || 0}
              color="cyan"
            />
            <MetricCard
              title="Creators $5K+ /mo"
              value={monetization?.creators_5k_plus || 0}
              color="blue"
            />
            <MetricCard
              title="Creators $10K+ /mo"
              value={monetization?.creators_10k_plus || 0}
              color="purple"
            />
          </div>
        </div>

        {/* F) Internal Self-Marketing Performance */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <LineChartIcon className="w-6 h-6 text-pink-400" />
            Internal Self-Marketing
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {selfMarketing.map((brand, idx) => (
              <div key={idx} className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-4">{brand.brand_name}</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Active Campaigns</span>
                    <span className="text-white font-medium">{brand.active_campaigns}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Posts (7d / 30d)</span>
                    <span className="text-white font-medium">{brand.posts_7d} / {brand.posts_30d}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Impressions (30d)</span>
                    <span className="text-cyan-400 font-medium">{formatNumber(brand.impressions_30d)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Signups (30d)</span>
                    <span className="text-emerald-400 font-medium">{brand.signups_30d || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Top Platform</span>
                    <span className="text-white font-medium capitalize">{brand.top_platform}</span>
                  </div>
                  {brand.campaign_names?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-xs text-gray-400 mb-2">Active Campaigns:</p>
                      {brand.campaign_names.map((name, i) => (
                        <p key={i} className="text-xs text-gray-300">• {name}</p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* G) Valuation Snapshot */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-purple-400" />
            Valuation Snapshot
          </h2>
          {valuation?.latest_snapshot ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Latest Valuation */}
              <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Latest Valuation
                  <span className="text-sm font-normal text-gray-400 ml-2">
                    ({valuation.latest_snapshot.snapshot_date})
                  </span>
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-400">Estimated Low</p>
                    <p className="text-2xl font-bold text-red-400">
                      {formatCurrency(valuation.latest_snapshot.estimated_value_low)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Estimated Mid</p>
                    <p className="text-3xl font-bold text-cyan-400">
                      {formatCurrency(valuation.latest_snapshot.estimated_value_mid || 
                        (valuation.latest_snapshot.estimated_value_low + valuation.latest_snapshot.estimated_value_high) / 2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Estimated High</p>
                    <p className="text-2xl font-bold text-green-400">
                      {formatCurrency(valuation.latest_snapshot.estimated_value_high)}
                    </p>
                  </div>
                  {valuation.latest_snapshot.notes && (
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-xs text-gray-400 mb-1">Notes:</p>
                      <p className="text-sm text-gray-300">{valuation.latest_snapshot.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Valuation Timeline */}
              <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Valuation Timeline</h3>
                <div className="space-y-3">
                  {valuation.all_snapshots?.slice(0, 6).map((snapshot, idx) => (
                    <div key={idx} className="flex justify-between items-center text-sm">
                      <span className="text-gray-400">{snapshot.snapshot_date}</span>
                      <div className="text-right">
                        <p className="text-white font-medium">
                          {formatCurrency(snapshot.estimated_value_mid || 
                            (snapshot.estimated_value_low + snapshot.estimated_value_high) / 2)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatCurrency(snapshot.estimated_value_low)} - {formatCurrency(snapshot.estimated_value_high)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-12 text-center">
              <p className="text-gray-400 mb-4">No valuation data available</p>
              <button
                onClick={seedValuationData}
                className="px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-lg text-purple-300 hover:bg-purple-500/30 transition-colors"
              >
                Seed Mock Data
              </button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

// Helper Components
function KPICard({ title, value, format = 'number', icon, color = 'blue', note }) {
  const colors = {
    blue: 'from-blue-500 to-cyan-500',
    green: 'from-green-500 to-emerald-500',
    emerald: 'from-emerald-500 to-green-500',
    cyan: 'from-cyan-500 to-blue-500',
    yellow: 'from-yellow-500 to-orange-500',
    purple: 'from-purple-500 to-pink-500'
  };

  const formatValue = () => {
    if (format === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    } else if (format === 'percentage') {
      return `${value}%`;
    } else {
      return new Intl.NumberFormat('en-US').format(value);
    }
  };

  return (
    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-6 hover:border-white/20 transition-colors">
      <div className={`inline-flex p-2 rounded-lg bg-gradient-to-br ${colors[color]} bg-opacity-20 mb-3`}>
        {icon}
      </div>
      <p className="text-sm text-gray-400 mb-1">{title}</p>
      <p className="text-2xl font-bold text-white">{formatValue()}</p>
      {note && <p className="text-xs text-gray-500 mt-1">{note}</p>}
    </div>
  );
}

function MetricCard({ title, value, format = 'number', color = 'cyan' }) {
  const colors = {
    cyan: 'text-cyan-400',
    emerald: 'text-emerald-400',
    blue: 'text-blue-400',
    purple: 'text-purple-400'
  };

  const formatValue = () => {
    if (format === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0
      }).format(value);
    }
    return new Intl.NumberFormat('en-US').format(value);
  };

  return (
    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-4">
      <p className="text-sm text-gray-400 mb-2">{title}</p>
      <p className={`text-3xl font-bold ${colors[color]}`}>{formatValue()}</p>
    </div>
  );
}
